import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  WHOLESALE_DOCUMENT_LABELS,
  WHOLESALE_TARGET_LABELS,
  DRIVER_COMMISSIONS,
  DRIVER_COMMISSION_LABELS,
  SELLER_COMMISSIONS,
  SELLER_COMMISSION_LABELS,
  BIFI_NEW_CUSTOMERS,
  BIFI_NEW_CUSTOMER_LABELS,
  type BifiNewCustomers,
  type DriverCommission,
  type GeoLocationDTO,
  type SellerCommission,
  type WholesaleDocument,
  type WholesaleSettingsDTO,
  type WholesaleSettlementTarget,
  type WholesaleTagDTO,
  type WholesaleVehicleDTO,
} from '@bifeai/shared';
import { Button, Callout, Card, CardBody, CardHeader, Field, Input, PageHeader, Select, Skeleton, Textarea, useToast } from '@bifeai/ui';
import { Trash2, Truck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { LocationPicker } from '../../components/map/LocationPicker';
import { api, errorMessage } from '../../lib/api';
import { useSession } from '../../lib/session';
import { moneyInput, parseMoney, usePriceLists, useTags, useVehicles, useWsSettings } from './common';
import '../orders-chat/orders-chat.css';
import './wholesale.css';
import { RemcarneSettingsCard } from './remcarne';

/** Configuración del Mayorista: de dónde sale la mercadería, a dónde entra la rendición, tipos de negocio y zonas. */
export function WholesaleSettingsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { me, can } = useSession();
  const settings = useWsSettings();
  const lists = usePriceLists();
  const own = me.branches.filter((b) => !b.franchise);
  const [v, setV] = useState<WholesaleSettingsDTO | null>(null);
  useEffect(() => {
    if (settings.data && !v) setV(settings.data);
  }, [settings.data, v]);
  const save = useMutation({
    mutationFn: () => api.put<WholesaleSettingsDTO>('/wholesale/settings', { ...v! }),
    onSuccess: async (s) => {
      setV(s);
      await qc.invalidateQueries({ queryKey: ['wholesale'] });
      toast.success('Configuración guardada');
    },
    onError: (e) => toast.error('No se pudo guardar', errorMessage(e)),
  });
  const owner = can('settings.manage');
  return (
    <>
      <PageHeader title="Configuración del mayorista" subtitle="Lo decide el dueño. Vale para todos los pedidos y cobros del canal mayorista." />
      <Card>
        <CardHeader title="General" />
        <CardBody>
          {!v ? (
            <Skeleton style={{ height: 160 }} />
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              <div className="bf-ws-form">
                <Field label="Sale la mercadería de" hint="Se puede cambiar en cada pedido.">
                  <Select value={v.originBranchId ?? ''} disabled={!owner} onChange={(e) => setV({ ...v, originBranchId: e.target.value || null })}>
                    <option value="">La casa central</option>
                    {own.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Lista general" hint="La que se usa si el cliente, su tipo de negocio y su zona no tienen una.">
                  <Select
                    value={v.defaultPriceListId ?? ''}
                    disabled={!owner}
                    onChange={(e) => setV({ ...v, defaultPriceListId: e.target.value || null })}
                  >
                    <option value="">Precio del mostrador</option>
                    {(lists.data ?? []).map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Comprobante por defecto">
                  <Select
                    value={v.defaultDocument}
                    disabled={!owner}
                    onChange={(e) => setV({ ...v, defaultDocument: e.target.value as WholesaleDocument })}
                  >
                    <option value="REMITO">{WHOLESALE_DOCUMENT_LABELS.REMITO}</option>
                    <option value="INVOICE">{WHOLESALE_DOCUMENT_LABELS.INVOICE}</option>
                  </Select>
                </Field>
                <Field label="Orden de las paradas en la hoja de ruta" hint="Siempre se puede cambiar a mano con las flechas.">
                  <Select value={v.routeOrder} disabled={!owner} onChange={(e) => setV({ ...v, routeOrder: e.target.value as 'AUTO' | 'MANUAL' })}>
                    <option value="AUTO">Automático: por cercanía desde el local</option>
                    <option value="MANUAL">A mano (con el botón para ordenar por cercanía)</option>
                  </Select>
                </Field>
                <Field label="Hora de salida de las camionetas" hint="Para estimar a qué hora llega a cada cliente.">
                  <Input
                    type="time"
                    value={v.routeStartTime}
                    disabled={!owner}
                    onChange={(e) => setV({ ...v, routeStartTime: e.target.value || '08:00' })}
                  />
                </Field>
                <Field label="Minutos en cada parada" hint="Descarga y cobro.">
                  <Input
                    inputMode="numeric"
                    value={String(v.stopMinutes)}
                    disabled={!owner}
                    onChange={(e) => setV({ ...v, stopMinutes: Math.max(0, Math.min(120, Number(e.target.value.replace(/\D/g, '')) || 0)) })}
                  />
                </Field>
              </div>
              {me.tenant.modules.includes('ORDERS_CHAT') && (
                <>
                  <strong>bifi para mayoristas</strong>
                  <div className="bf-ws-form">
                    <Field
                      label="Atender mayoristas por WhatsApp"
                      hint="Reconoce al cliente mayorista por su teléfono y le toma el pedido con su lista."
                    >
                      <Select
                        value={v.bifiEnabled ? '1' : '0'}
                        disabled={!owner}
                        onChange={(e) => setV({ ...v, bifiEnabled: e.target.value === '1' })}
                      >
                        <option value="0">No</option>
                        <option value="1">Sí</option>
                      </Select>
                    </Field>
                    {v.bifiEnabled && (
                      <>
                        <Field
                          label="Un mayorista nuevo, hasta que lo aprobás, ve"
                          hint="Así nadie consigue precios mayoristas solo con decir que es un negocio."
                        >
                          <Select
                            value={v.bifiNewCustomers}
                            disabled={!owner}
                            onChange={(e) => setV({ ...v, bifiNewCustomers: e.target.value as BifiNewCustomers })}
                          >
                            {BIFI_NEW_CUSTOMERS.map((k) => (
                              <option key={k} value={k}>
                                {BIFI_NEW_CUSTOMER_LABELS[k]}
                              </option>
                            ))}
                          </Select>
                        </Field>
                        <Field label="Pedido mínimo mayorista" hint="Vacío o 0 = sin mínimo.">
                          <Input
                            inputMode="decimal"
                            value={moneyInput(v.bifiMinOrderCents || null)}
                            disabled={!owner}
                            onChange={(e) => setV({ ...v, bifiMinOrderCents: parseMoney(e.target.value) ?? 0 })}
                          />
                        </Field>
                        <Field label="Le puede decir cuánto debe">
                          <Select
                            value={v.bifiShowBalance ? '1' : '0'}
                            disabled={!owner}
                            onChange={(e) => setV({ ...v, bifiShowBalance: e.target.value === '1' })}
                          >
                            <option value="1">Sí, si pregunta</option>
                            <option value="0">No</option>
                          </Select>
                        </Field>
                      </>
                    )}
                  </div>
                  {v.bifiEnabled && (
                    <>
                      <Field
                        label="Indicaciones para mayoristas (opcional)"
                        hint="Se suman a las de siempre. El tono y el nombre de bifi salen de Configurar agente."
                      >
                        <Textarea
                          rows={3}
                          value={v.bifiInstructions ?? ''}
                          disabled={!owner}
                          placeholder="Ej.: a los restaurantes ofreceles la promo de vacío por caja; los pedidos para el día siguiente se toman hasta las 18."
                          onChange={(e) => setV({ ...v, bifiInstructions: e.target.value || null })}
                        />
                      </Field>
                      <Callout tone="info">
                        bifi atiende como mayorista a quien tiene su teléfono cargado en un cliente mayorista. Si querés un número solo para
                        mayoristas, conectá otro WhatsApp y marcalo como "Solo mayoristas" en Configurar agente: se cobra como un número más de bifi.
                      </Callout>
                    </>
                  )}
                </>
              )}
              <strong>Comisiones</strong>
              <div className="bf-ws-form">
                <Field label="Vendedor" hint="Cada persona puede tener su propio % (en Comisiones).">
                  <Select
                    value={v.sellerCommission}
                    disabled={!owner}
                    onChange={(e) => setV({ ...v, sellerCommission: e.target.value as SellerCommission })}
                  >
                    {SELLER_COMMISSIONS.map((k) => (
                      <option key={k} value={k}>
                        {SELLER_COMMISSION_LABELS[k]}
                      </option>
                    ))}
                  </Select>
                </Field>
                {v.sellerCommission !== 'NONE' && (
                  <Field label="% general del vendedor">
                    <Input
                      inputMode="decimal"
                      value={String(v.sellerCommissionPct).replace('.', ',')}
                      disabled={!owner}
                      onChange={(e) =>
                        setV({
                          ...v,
                          sellerCommissionPct: Math.max(0, Math.min(100, Number(e.target.value.replace(',', '.').replace(/[^\d.]/g, '')) || 0)),
                        })
                      }
                    />
                  </Field>
                )}
                <Field label="Repartidor">
                  <Select
                    value={v.driverCommission}
                    disabled={!owner}
                    onChange={(e) => setV({ ...v, driverCommission: e.target.value as DriverCommission })}
                  >
                    {DRIVER_COMMISSIONS.map((k) => (
                      <option key={k} value={k}>
                        {DRIVER_COMMISSION_LABELS[k]}
                      </option>
                    ))}
                  </Select>
                </Field>
                {(v.driverCommission === 'FIXED' || v.driverCommission === 'BOTH') && (
                  <Field label="Por cada entrega">
                    <Input
                      inputMode="decimal"
                      value={moneyInput(v.driverFixedCents)}
                      disabled={!owner}
                      onChange={(e) => setV({ ...v, driverFixedCents: parseMoney(e.target.value) ?? 0 })}
                    />
                  </Field>
                )}
                {(v.driverCommission === 'PERCENT' || v.driverCommission === 'BOTH') && (
                  <Field label="% de lo entregado">
                    <Input
                      inputMode="decimal"
                      value={String(v.driverCommissionPct).replace('.', ',')}
                      disabled={!owner}
                      onChange={(e) =>
                        setV({
                          ...v,
                          driverCommissionPct: Math.max(0, Math.min(100, Number(e.target.value.replace(',', '.').replace(/[^\d.]/g, '')) || 0)),
                        })
                      }
                    />
                  </Field>
                )}
              </div>
              <div className="bf-ws-form">
                <Field label="La plata que rinden repartidores y vendedores entra a">
                  <Select
                    value={v.settlementTarget}
                    disabled={!owner}
                    onChange={(e) => setV({ ...v, settlementTarget: e.target.value as WholesaleSettlementTarget })}
                  >
                    <option value="WHOLESALE_CASH">{WHOLESALE_TARGET_LABELS.WHOLESALE_CASH} (recomendado)</option>
                    <option value="BRANCH_CASH">{WHOLESALE_TARGET_LABELS.BRANCH_CASH}</option>
                  </Select>
                </Field>
                {v.settlementTarget === 'BRANCH_CASH' && (
                  <Field label="Sucursal">
                    <Select
                      value={v.settlementBranchId ?? ''}
                      disabled={!owner}
                      onChange={(e) => setV({ ...v, settlementBranchId: e.target.value || null })}
                    >
                      <option value="">Elegí</option>
                      {own.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
              </div>
              <Callout tone="info">
                Siempre queda identificado como rendición mayorista (con el nombre de quién rindió). Aunque entre a la caja de una sucursal, cuenta
                como venta mayorista y no del mostrador. Los cobros en efectivo en la oficina van al mismo lugar.
              </Callout>
              {owner && (
                <div>
                  <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
                    Guardar
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>
      {v && <RemcarneSettingsCard v={v} setV={setV} owner={owner} onSave={() => save.mutate()} saving={save.isPending} />}
      <div className="bf-dash-grid">
        <TagCard kind="segments" title="Tipos de negocio" hint="Restaurante, carnicería, colegio… Cada uno puede tener su lista." />
        <TagCard kind="zones" title="Zonas" hint="Zonas de reparto o de venta. Cada una puede tener su lista." />
        <VehiclesCard />
        <OriginCard key={v?.originBranchId ?? 'hq'} />
      </div>
    </>
  );
}

function TagCard({ kind, title, hint }: { kind: 'segments' | 'zones'; title: string; hint: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const tags = useTags(kind);
  const lists = usePriceLists();
  const [name, setName] = useState('');
  const [listId, setListId] = useState('');
  const done = async () => qc.invalidateQueries({ queryKey: ['wholesale'] });
  const add = useMutation({
    mutationFn: () => api.post<WholesaleTagDTO[]>(`/wholesale/tags/${kind}`, { name, priceListId: listId || null }),
    onSuccess: async () => {
      await done();
      setName('');
      setListId('');
    },
    onError: (e) => toast.error('No se pudo agregar', errorMessage(e)),
  });
  const update = useMutation({
    mutationFn: (t: WholesaleTagDTO) => api.put(`/wholesale/tags/${kind}/${t.id}`, { name: t.name, priceListId: t.priceListId }),
    onSuccess: done,
    onError: (e) => toast.error('No se pudo guardar', errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/wholesale/tags/${kind}/${id}`),
    onSuccess: done,
    onError: (e) => toast.error('No se pudo quitar', errorMessage(e)),
  });
  return (
    <Card>
      <CardHeader title={title} subtitle={hint} />
      <CardBody>
        <div style={{ display: 'grid', gap: 8 }}>
          {(tags.data ?? []).map((t) => (
            <div key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ minWidth: 140 }}>{t.name}</strong>
              <span className="bf-cell-sub">{t.customers} clientes</span>
              <Select
                value={t.priceListId ?? ''}
                onChange={(e) => update.mutate({ ...t, priceListId: e.target.value || null })}
                style={{ width: 'auto', minWidth: 170 }}
                aria-label={`Lista de ${t.name}`}
              >
                <option value="">Sin lista propia</option>
                {(lists.data ?? []).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </Select>
              <button type="button" className="bf-order-del" aria-label={`Quitar ${t.name}`} onClick={() => remove.mutate(t.id)}>
                <Trash2 size={14} aria-hidden />
              </button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <Input
              placeholder="Nuevo"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ maxWidth: 200 }}
              aria-label={`Nuevo ${title}`}
            />
            <Select value={listId} onChange={(e) => setListId(e.target.value)} style={{ width: 'auto', minWidth: 170 }} aria-label="Lista">
              <option value="">Sin lista propia</option>
              {(lists.data ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </Select>
            <Button loading={add.isPending} disabled={name.trim().length < 2} onClick={() => add.mutate()}>
              Agregar
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function VehiclesCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const vehicles = useVehicles();
  const [name, setName] = useState('');
  const [plate, setPlate] = useState('');
  const done = async () => qc.invalidateQueries({ queryKey: ['wholesale', 'vehicles'] });
  const add = useMutation({
    mutationFn: () => api.post<WholesaleVehicleDTO[]>('/wholesale/vehicles', { name, plate: plate || null }),
    onSuccess: async () => {
      await done();
      setName('');
      setPlate('');
    },
    onError: (e) => toast.error('No se pudo agregar', errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/wholesale/vehicles/${id}`),
    onSuccess: done,
    onError: (e) => toast.error('No se pudo quitar', errorMessage(e)),
  });
  return (
    <Card>
      <CardHeader title="Camionetas" subtitle="Las que salen a repartir. Se eligen al armar cada hoja de ruta." />
      <CardBody>
        <div style={{ display: 'grid', gap: 8 }}>
          {(vehicles.data ?? []).map((v) => (
            <div key={v.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Truck size={16} aria-hidden />
              <strong style={{ minWidth: 140 }}>{v.name}</strong>
              <span className="bf-cell-sub">{v.plate ?? 'Sin patente'}</span>
              <button type="button" className="bf-order-del" aria-label={`Quitar ${v.name}`} onClick={() => remove.mutate(v.id)}>
                <Trash2 size={14} aria-hidden />
              </button>
            </div>
          ))}
          {vehicles.data?.length === 0 && <span className="bf-cell-sub">Todavía no cargaste camionetas.</span>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <Input
              placeholder="Nombre (ej. Kangoo blanca)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ maxWidth: 220 }}
              aria-label="Nombre de la camioneta"
            />
            <Input placeholder="Patente" value={plate} onChange={(e) => setPlate(e.target.value)} style={{ maxWidth: 130 }} aria-label="Patente" />
            <Button loading={add.isPending} disabled={name.trim().length < 2} onClick={() => add.mutate()}>
              Agregar
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function OriginCard() {
  const q = useQuery({
    queryKey: ['wholesale', 'origin-location'],
    queryFn: () => api.get<GeoLocationDTO>('/wholesale/origin/location'),
  });
  return (
    <Card>
      <CardHeader title="Ubicación del local de salida" subtitle="De acá arrancan las camionetas: el recorrido por cercanía empieza en este punto." />
      <CardBody>
        {!q.data ? <Skeleton style={{ height: 220 }} /> : <LocationPicker base="/wholesale/origin" initial={q.data} canSearch={false} />}
      </CardBody>
    </Card>
  );
}
